import React, { useEffect, useMemo, useState  } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getGSTR2BSummary,
  getGSTR2BDetails,
  getRCMSummary,
  getRCMDetails,
  uploadGSTR2BExcel,
  uploadRCMExcel,
  getUploadedTime,
  completeClick,
  getCompleteWithin,
  getCompareResults,
  getAllGSTR2B,
  getExportData
} from '../api/misApis';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import  "../Assets/Css/alert.css"

const getRCMInvoiceMonth = (row) => {
  if (row?.Upload_Month === null || row?.Upload_Month === undefined) {
    return null;
  }
  return Number(row.Upload_Month);
};

function GstReco() {
  const [summary, setSummary] = useState([]);
  const [details, setDetails] = useState([]);
  const [rcmSummary, setRcmSummary] = useState([]);
  const [rcmDetails, setrcmDetails] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedRCMSupplier, setSelectedRCMSupplier] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadedTime, setUploadedTime] = useState(null);
  const [completeWithin, setCompleteWithin] = useState(null);
  const [rcmAndLogic, setCompareResults] = useState([]);

   // 🔽 New: month filter state
  const [uploadMonth, setUploadMonth] = useState('');
  // NEW: full invoice-level dataset (GSTReco_Output_InvByMonth)
  const [allGSTR2B, setAllGSTR2B] = useState([]);


    // helpers
  const getUploadMonthFromRow = (row) =>
    row?.Upload_Month ?? row?.UploadMonth ?? null; // tolerant to both names
  
  const navigate = useNavigate();

  const rowsPerPage = 10;

  useEffect(() => {
    getGSTR2BSummary()
      .then((res) => setSummary(res.data))
      .catch((err) => console.error(err));
    getRCMSummary()
      .then((res) => setRcmSummary(res.data))
      .catch((err) => console.error(err));
        // fetch once on mount
  getAllGSTR2B()
    .then(res => setAllGSTR2B(Array.isArray(res.data) ? res.data : []))
    .catch(err => console.error('Failed to fetch All GST data', err));
  }, []);

const getMonthFromAnyRow = (row) =>
  row?.Upload_Month ?? row?.UploadMonth ?? row?.Month_Logic ?? row?.Month_GSTR2B ?? null;

// When a month is applied, show all invoices of that month in the details table.
// When month is cleared, revert to empty details and no supplier selected.
useEffect(() => {
  if (uploadMonth) {
    // Clear supplier selection; we’ll show ALL suppliers’ invoices for this month
    setSelectedSupplier('');
    const monthRows = allGSTR2B.filter(r => String(getMonthFromAnyRow(r)) === String(uploadMonth));
    setDetails(monthRows); // Invoice Details now shows ALL invoices for the selected month
  } else {
    // Month cleared → restore original behaviour (no details until supplier click)
    setDetails([]);
    setSelectedSupplier('');
  }
}, [uploadMonth, allGSTR2B]);


   // distinct months for dropdown (sorted asc, strings)
  const uniqueMonths = useMemo(() => {
    const vals = summary
      .map(getUploadMonthFromRow)
      .filter((v) => v !== null && v !== undefined && String(v).trim() !== '');
    const uniq = Array.from(new Set(vals.map(String)));
    // numeric sort if possible, else lexicographic
    return uniq.sort((a, b) => Number(a) - Number(b));
  }, [summary]);

  // handlers
  const handleSupplierClick = (supplier) => {
    setSelectedSupplier(supplier);
      // If month filter is active, filter from pre-fetched month rows
  if (uploadMonth) {
    const monthRows = allGSTR2B.filter(r => String(getMonthFromAnyRow(r)) === String(uploadMonth));
    const supplierKey = (r) =>
      String(
        r?.Supplier_Logic ??
        r?.Supplier_GSTR2B ??
        r?.SupplierName ??
        r?.TradeLegalname ??
        ''
      ).trim();

    const filtered = monthRows.filter(r => supplierKey(r) === String(supplier).trim());
    setDetails(filtered);
    return;
  }
    getGSTR2BDetails(supplier)
      .then((res) => setDetails(res.data))
      .catch((err) => console.error(err));
  };

  const handleRCMSupplierClick = (supplier) => {
    setSelectedRCMSupplier(supplier);

    getRCMDetails(supplier)
      .then((res) => {
        let rows = Array.isArray(res.data) ? res.data : [];

        if (uploadMonth) {
          const monthNum = Number(uploadMonth);

          rows = rows.filter(
            (row) => getRCMInvoiceMonth(row) === monthNum
          );
        }

        setrcmDetails(rows);
      })
      .catch((err) => {
        console.error("RCM Details Error:", err);
        setrcmDetails([]);
      });
  };

  const filterRows = (rows) => {
    if (!searchTerm.trim()) return rows;
    return rows.filter((row) =>
      Object.values(row).some((val) =>
        String(val).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

   // apply month filter first, then search
  const monthFilteredSummary = useMemo(() => {
    if (!uploadMonth) return summary;
    return summary.filter((row) => String(getUploadMonthFromRow(row)) === String(uploadMonth));
  }, [summary, uploadMonth]);


  const filteredSummary = filterRows(monthFilteredSummary);
  const filteredDetails = filterRows(details);
  //const filteredRCMSummary = filterRows(rcmSummary);

  const getRCMUploadMonth = (row) => row?.Upload_Month ?? null;

  const monthFilteredRCMSummary = useMemo(() => {
    if (!uploadMonth) return rcmSummary;

    return rcmSummary.filter(
      (row) => String(getRCMUploadMonth(row)) === String(uploadMonth)
    );
  }, [rcmSummary, uploadMonth]);

  const filteredRCMSummary = filterRows(monthFilteredRCMSummary);

  const filteredRCMDetails = filterRows(rcmDetails);
  // const filterRCMandLogic = filterRows(rcmAndLogic);

  const gstrColumns = [
    "GSTIN_GSTR2B", "Supplier_GSTR2B", "Month_GSTR2B", "InvoiceValue_GSTR2B", "TaxableValue_GSTR2B",
    "IntegratedTax_GSTR2B", "CentralTax_GSTR2B", "StateUTTax_GSTR2B",
    "GSTIN_Logic", "Supplier_Logic", "Month_Logic", "InvoiceValue_Logic", "TaxableValue_Logic",
    "IntegratedTax_Logic", "CentralTax_Logic", "StateUTTax_Logic",
    "InvoiceValueDiff", "TaxableValueDiff", "IntegratedTaxDiff", "CentralTaxDiff", "StateUTTaxDiff", "DiffFlag"
  ];

  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentRows = filteredSummary.slice(startIndex, startIndex + rowsPerPage);
  const totalPages = Math.ceil(filteredSummary.length / rowsPerPage);

  // Export All table data in .xlsx
  /*const handleExport = () => {
    const wb = XLSX.utils.book_new();
    const sheets = [
      { name: "GSTR-2B Summary", data: filteredSummary },
      { name: "GSTR-2B Details", data: filteredDetails },
      { name: "RCM Summary", data: filteredRCMSummary },
      { name: "RCM Details", data: filteredRCMDetails },
      {name : "RCM & Logic ", data : rcmAndLogic}
    ];

    sheets.forEach(sheet => {
      const ws = XLSX.utils.json_to_sheet(sheet.data);
      XLSX.utils.book_append_sheet(wb, ws, sheet.name);
    });
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const fileData = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(fileData, 'GST_Reconciliation_Report.xlsx');
  };*/

  const handleExport = async () => {
    try {
      // Optional: show a loading toast/state here if you want
      showToast('Preparing Excel file, please wait...', 'success');
      
      // 1. Fetch all tables from backend
      const res = await getExportData(uploadMonth);
      const { sheet1, sheet2, sheet3, sheet4, sheet5 } = res.data;

      // 5. Create Excel Workbook
      const wb = XLSX.utils.book_new();
      const sheets = [
        { name: "Overall", data: sheet1 },
        { name: "RCM", data: sheet2 },
        { name: "Quarterly", data: sheet3 },
        { name: "Overall-RCM-Quarterly", data: sheet4 },
        { name: "InvoiceWise", data: sheet5 }
      ];

      sheets.forEach(sheet => {
        // If data is empty, insert a dummy row so sheet doesn't crash
        const sheetData = sheet.data.length > 0 ? sheet.data : [{ Message: "No data available for selected month" }];
        const ws = XLSX.utils.json_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(wb, ws, sheet.name);
      });

      // 6. Download the file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const fileData = new Blob([excelBuffer], { type: 'application/octet-stream' });
      
      // Dynamic file name based on month selection
      const fileName = uploadMonth 
        ? `GST_Reconciliation_Month_${uploadMonth}.xlsx` 
        : 'GST_Reconciliation_All_Months.xlsx';
        
      saveAs(fileData, fileName);
      showToast('Excel downloaded successfully!', 'success');

    } catch (error) {
      console.error("Export Error:", error);
      showToast('Failed to export data', 'danger');
    }
  };

const showToast = (message, type = 'success') => {
  const alertPlaceholder = document.getElementById("alertPlaceholder");

  const isSuccess = type === 'success';
  const bgClass = isSuccess ? 'bg-green-100' : 'bg-red-100';
  const borderClass = isSuccess ? 'border-green-400' : 'border-red-400';
  const textClass = isSuccess ? 'text-green-700' : 'text-red-700';
  const icon = isSuccess ? '✅ Success:' : '❌ Error:';

  alertPlaceholder.innerHTML = `
    <div class="alert-box ${bgClass} ${borderClass} ${textClass} px-4 py-3 rounded shadow-lg text-left animate-fadeIn w-fit mx-auto">
      <strong class="font-bold">${icon}</strong>
      <span class="block sm:inline ml-1">${message}</span>
    </div>
  `;

  setTimeout(() => {
    alertPlaceholder.innerHTML = "";
  }, 2500);
};


  ///Import function for GSTR2B Excel file
  const importGSTR2BExcelHandler = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
     await uploadGSTR2BExcel(file);
      const res = await getGSTR2BSummary();
      setSummary(res.data);

      showToast('GSTR-2B uploaded successfully', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to upload GSTR-2B', 'danger');
    }
    setLoading(false);
    event.target.value = null;
  };

  ///Import function for RCM Excel file
  const importRCMExcelHandler = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      await uploadRCMExcel(file);
      const res = await getRCMSummary();
      setRcmSummary(res.data);

      showToast('RCM uploaded successfully', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to upload RCM', 'danger');
    }
    setLoading(false);
    event.target.value = null;
  };

// fetch upload time 
useEffect(() => {
  getUploadedTime()
    .then(res => {
    const lastUploaded = res.data?.[0]?.Uploaded_Date;
    setUploadedTime(lastUploaded || null);
  })
    .catch(err => console.error("Failed to fetch uploaded time", err));
}, []); // ✅ Run only once on mount (empty dependency array)

//Fetch Complete before time 
useEffect(() => {
  getCompleteWithin()
    .then(res => {
    const completeWithin = res.data?.[0]?.CompleteWithin;
    setCompleteWithin(completeWithin || null);
  })
    .catch(err => console.error("Failed to fetch Complete time", err));
}, []); // ✅ Run only once on mount (empty dependency array)

// Get RCM & Logic table data
  useEffect(() => {
    getCompareResults()
      .then((res) => setCompareResults(res.data))
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
  if (!uploadMonth) {
    setSelectedRCMSupplier('');
    setrcmDetails([]);
  }
}, [uploadMonth]);

  return (
    <div className="p-4">
    <div id="alertPlaceholder"></div>
   <div className="mb-4 flex justify-between items-center flex-wrap gap-4">
  <button
    onClick={() => navigate(-1)}
    className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-800 text-sm"
  >
    ⬅️ Back to Home
  </button>

  <div className="flex flex-wrap items-center gap-3 text-sm">
    <span className="font-medium">🕒 Uploaded:</span>
    <span className="text-gray-700">{uploadedTime || "Loading..."}</span>
    
    <span className="font-medium">🕒 Complete Before:</span>
    <span className="text-gray-700">{completeWithin || "Loading..."}</span>

    <button
      onClick={async () => {
        try {
          await completeClick();
          showToast("Marked as completed!", "success");
        } catch (err) {
          console.error(err);
          showToast("Already Mark As Complete", "danger");
        }
      }}
      className="px-3 py-1 bg-green-700 text-white rounded hover:bg-green-800"
    >
      ✅ Mark Complete
    </button>
  </div>
</div>


      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <input
          type="text"
          placeholder="🔍 Search across all tables..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="p-2 border rounded shadow-sm text-sm flex-1"
        />
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
        >📤 Export to Excel</button>
        <label className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm cursor-pointer">
          📥 Upload GSTR2B
          <input type="file" className="hidden" onChange={importGSTR2BExcelHandler} />
        </label>
        <label className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm cursor-pointer">
          📥 Upload RCM
          <input type="file" className="hidden" onChange={importRCMExcelHandler} />
        </label>
      </div>
 

    {/* {loading && (
    <div className="flex justify-center items-center mb-4">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div>
      <span className="ml-2 text-sm text-blue-600">Uploading...</span>
    </div>
  )} */}

      {loading && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-70 backdrop-blur-sm">
    <div className="flex flex-col items-center space-y-2">
      <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-600"></div>
      <span className="text-blue-700 font-medium">Uploading, please wait...</span>
    </div>
  </div>
)}

{/* <div className="items-center space-x-2" style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
  <span className="text-sm font-medium">🕒 Uploaded:</span>
  <span className="text-sm text-gray-700">{uploadedTime || "Loading..."}</span>
<span className="text-sm font-medium">🕒 Complete Before:</span>
  <span className="text-sm text-gray-700">{completeWithin || "Loading..."}</span>
  
  <button
    onClick={async () => {
      try {
        await completeClick();
        showToast("Marked as completed!", "success");
      } catch (err) {
        console.error(err);
        showToast("Already Mark As Complete", "danger");
      }
    }}
    className="px-3 py-1 bg-green-700 text-white rounded hover:bg-green-800 text-sm"
  >
    ✅ Mark Complete
  </button>
</div> */}

      {/* GSTR-2B Summary */}
      <h2 className="text-xl font-semibold mb-4">GSTR-2B Summary</h2>
        {/* 🔽 New: Upload Month filter */}
        <div className="flex items-center gap-2 mb-5">
          <label className="text-sm font-medium">📅 Upload Month:</label>
          <select
            value={uploadMonth}
            onChange={(e) => {
              setUploadMonth(e.target.value);
              setCurrentPage(1);
            }}
            className="border px-3 py-1 rounded text-sm shadow-sm"
          >
            <option value="">All</option>
            {uniqueMonths.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          {uploadMonth && (
            <button
              onClick={() => { setUploadMonth(''); setCurrentPage(1); }}
              className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
              title="Clear month filter"
            >
              Clear
            </button>
          )}
        </div>
      <div className="overflow-auto border rounded shadow">
        <table className="min-w-[1200px] text-sm border-collapse w-full">
          <thead className="bg-gray-100 font-bold">
            <tr>
              {gstrColumns.map((col) => (
                <th key={col} className="px-2 py-2 border">{col.replace(/_/g, ' ')}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((item, index) => (
              <tr
                key={index}
                className="hover:bg-gray-100 cursor-pointer"
                onClick={() => handleSupplierClick(item.Supplier_Logic || item.Supplier_GSTR2B)}
              >
                {gstrColumns.map((col) => (
                  <td
                    key={col}
                    className={`px-2 py-1 border text-xs ${
                      col === 'DiffFlag'
                        ? item[col] === 'Red'
                          ? 'text-red-600 font-semibold'
                          : item[col] === 'Green'
                          ? 'text-green-600 font-semibold'
                          : ''
                        : 'text-gray-800'
                    }`}
                  >
                    {item[col]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex justify-between items-center">
        <span className="text-sm">Page {currentPage} of {totalPages}</span>
        <div className="space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
          >Prev</button>
          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
          >Next</button>
        </div>
      </div>

  {(uploadMonth || selectedSupplier) && (
        <div className="mt-6">
        <h3 className="font-semibold mb-2">
          Invoice Details
          {uploadMonth ? ` — Month: ${uploadMonth}` : null}
          {selectedSupplier ? ` — Supplier: ${selectedSupplier}` : ' — All Suppliers'}
         </h3>
          <div className="overflow-auto border rounded shadow">
            <table className="min-w-[1500px] text-sm border-collapse w-full">
              <thead className="bg-gray-100 font-bold">
                <tr>
                  {["InvoiceNo_GSTR2B", "Supplier_GSTR2B", "InvoiceDate_GSTR2B", "Month_GSTR2B",
                    "InvoiceValue_GSTR2B", "TaxableValue_GSTR2B", "IntegratedTax_GSTR2B", "CentralTax_GSTR2B", "StateUTTax_GSTR2B",
                    "InvoiceNo_Logic", "Supplier_Logic", "InvoiceDate_Logic", "Month_Logic",
                    "InvoiceValue_Logic", "TaxableValue_Logic", "IntegratedTax_Logic", "CentralTax_Logic", "StateUTTax_Logic",
                    "InvoiceValueDiff", "TaxableValueDiff", "IntegratedTaxDiff", "CentralTaxDiff", "StateUTTaxDiff", "DiffFlag"
                  ].map((col) => (
                    <th key={col} className="px-2 py-2 border whitespace-nowrap">{col.replace(/_/g, ' ')}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredDetails.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-100">
                    {["InvoiceNo_GSTR2B", "Supplier_GSTR2B", "InvoiceDate_GSTR2B", "Month_GSTR2B",
                      "InvoiceValue_GSTR2B", "TaxableValue_GSTR2B", "IntegratedTax_GSTR2B", "CentralTax_GSTR2B", "StateUTTax_GSTR2B",
                      "InvoiceNo_Logic", "Supplier_Logic", "InvoiceDate_Logic", "Month_Logic",
                      "InvoiceValue_Logic", "TaxableValue_Logic", "IntegratedTax_Logic", "CentralTax_Logic", "StateUTTax_Logic",
                      "InvoiceValueDiff", "TaxableValueDiff", "IntegratedTaxDiff", "CentralTaxDiff", "StateUTTaxDiff", "DiffFlag"
                    ].map((col) => (
                      <td key={col} className={`px-2 py-1 border text-xs ${
                        col === 'DiffFlag'
                          ? row[col] === 'Red'
                            ? 'text-red-600 font-semibold'
                            : row[col] === 'Green'
                            ? 'text-green-600 font-semibold'
                            : ''
                          : 'text-gray-800'
                      }`}>
                        {row[col]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    {/* RCM & Logic Summary */}
      <div className="mt-10">
        <h2 className="text-xl font-semibold mb-4">RCM & Logic Summary</h2>
        <div className="overflow-auto border rounded shadow">
          <table className="min-w-[1200px] text-sm border-collapse w-full">
            <thead className="bg-indigo-400 font-bold">
              <tr>
                {["Month_Portal", "RCM from Portal", "GST_Portal", "Month_Logic",
                  "RCM from Logic", "GST_Logic", "Diff"
                ].map((col) => (
                  <th key={col} className="px-2 py-2 border">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rcmAndLogic.map((item) => (
                <tr>
                    {["Month_Portal", "RCM from Portal", "GST_Portal", "Month_Logic",
                  "RCM from Logic", "GST_Logic", "Diff"
                ].map((col) => (
                    <td key={col} className="px-2 py-1 border text-xs text-gray-800">{item[col]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* RCM Summary */}
      <div className="mt-10">
        <h2 className="text-xl font-semibold mb-4">RCM Summary</h2>
        <div className="overflow-auto border rounded shadow">
          <table className="min-w-[1200px] text-sm border-collapse w-full">
            <thead className="bg-gray-100 font-bold">
              <tr>
                {["GSTINofsupplier", "TradeLegalname", "TotalInvoiceValue", "TotalTaxableValue",
                  "TotalIntegratedTax", "TotalCentralTax", "TotalStateUTTax", "TotalCess"
                ].map((col) => (
                  <th key={col} className="px-2 py-2 border">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRCMSummary.map((item, index) => {
                console.log("RCM SUMMARY ROW:", item);
                console.log("RCM SUMMARY TradeLegalname:", item.TradeLegalname);

                return (
                  <tr
                    key={index}
                    className="hover:bg-gray-100 cursor-pointer"
                    onClick={() => handleRCMSupplierClick(item.TradeLegalname)}
                  >
                    {[
                      "GSTINofsupplier",
                      "TradeLegalname",
                      "TotalInvoiceValue",
                      "TotalTaxableValue",
                      "TotalIntegratedTax",
                      "TotalCentralTax",
                      "TotalStateUTTax",
                      "TotalCess",
                    ].map((col) => (
                      <td
                        key={col}
                        className="px-2 py-1 border text-xs text-gray-800"
                      >
                        {item[col]}
                      </td>
                    ))}
                  </tr>
                );
              })}

            </tbody>
          </table>
        </div>
      </div>

      {/* RCM Child Table */}
      {selectedRCMSupplier && (
        <div className="mt-6">
          <h3 className="font-semibold mb-2">RCM Invoice Details for: {selectedRCMSupplier}</h3>
          <div className="overflow-auto border rounded shadow">
            <table className="min-w-[1500px] text-sm border-collapse w-full">
              <thead className="bg-gray-100 font-bold">
                <tr>
                  {["SupplierName", "InvoiceNumber", "InvoiceDate", "TotalInvoiceValue",
                    "TotalTaxableValue", "TotalIntegratedTax", "TotalCentralTax", "TotalStateUTTax",
                    "TotalCess"
                  ].map((col) => (
                    <th key={col} className="px-2 py-2 border whitespace-nowrap">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRCMDetails.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-100">
                    {["SupplierName", "InvoiceNumber", "InvoiceDate", "TotalInvoiceValue",
                      "TotalTaxableValue", "TotalIntegratedTax", "TotalCentralTax", "TotalStateUTTax",
                      "TotalCess"
                    ].map((col) => (
                      <td key={col} className="px-2 py-1 border text-xs">{row[col]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default GstReco;
